<template>
  <InfoOverlay
    :is-visible="showInfo"
    :title="title"
    :info="info"
    @close="() => (showInfo = false)"
  />
  <CardBase class="card-interactive" :class="{ fullscreen }" header-divider>
    <template #header>
      <CardInteractiveHeader
        :title="title"
        :titleType="titleType"
        :titleTypeFullscreen="titleTypeFullscreen"
        :showInfo="info !== undefined"
        :showControls="showControls"
        :enableFullscreen="enableFullscreen"
        :fullscreen="fullscreen"
        @toggle-show-info="onToggleInfoClick"
        @toggle-fullscreen="onToggleFullscreenClick"
      >
        <template #controls>
          <slot v-if="!isMobile" name="controls"></slot>
        </template>
        <div v-if="headerDivider" class="horizontal-divider"></div>
        <slot name="header"></slot>
      </CardInteractiveHeader>
    </template>
    <template v-if="isLoadingAfterRecord">
      <div :style="{ height: `${contentHeight}px` }" class="loader-container">
        <PulseLoader :loading="true" color="#3F46D6" />
      </div>
    </template>
    <template v-else>
      <div ref="contentRef" class="content-container">
        <slot></slot>
      </div>
    </template>
    <template #footer>
      <div class="card-interactive-footer" :class="fullscreen ? 'fullscreen' : ''">
        <div class="card-interactive-footer-content"><slot name="footer"></slot></div>
        <div v-if="fullscreen" class="card-interactive-footer-controller">
          <slot v-if="slots.footerControls" name="footerControls"></slot>
          <template v-else>
            {{ t('dashboard__text__selection') }} :
            <CollapseControllerSummary :showTimeSelection="true" :fullscreen="fullscreen" />
          </template>
        </div>
      </div>
    </template>
  </CardBase>
  <div v-if="fullscreen" class="overlay" @click="onToggleFullscreenClick"></div>
  <CardBase v-if="fullscreen" :title="title" :info="info" class="placeholder-card">
    <template #header>
      <CardInteractiveHeader
        :title="title"
        :titleType="titleType"
        :titleTypeFullscreen="titleTypeFullscreen"
        :showInfo="!!info"
      />
    </template>
    <div class="placeholder-content" :style="{ height: `${contentHeightNormal}px` }">
      <TextBase
        :text="t('dashboard__text-card-showing-in-fullscreen')"
        type="label"
        color="secondary"
      />
    </div>
  </CardBase>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import TextBase, { TextType } from '@/design-system/atoms/TextBase.vue'
import CardBase from './CardBase.vue'
import InfoOverlay from './InfoOverlay.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import CollapseControllerSummary from '../components/CollapseController/CollapseControllerSummary.vue'
import CardInteractiveHeader from './CardInteractiveHeader.vue'
import { useSlots } from 'vue'
import { useWindowWidth } from '@/composables/useWindowWidth'

const slots = useSlots()

const { t } = useI18n()

type Props = {
  title: string
  titleType?: TextType
  titleTypeFullscreen?: TextType
  info?: string
  enableFullscreen?: boolean
  showControls?: boolean
  isLoading?: boolean
  headerDivider?: boolean
}

type Emits = {
  (e: 'toggleFullscreen', fullscreen: boolean): void
  (e: 'toggleShowInfo', showInfo: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  titleType: 'paragraph',
  titleTypeFullscreen: 'h4',
  enableFullscreen: false,
  showControls: false,
  isLoading: false,
  headerDivider: false,
  info: undefined
})

const emit = defineEmits<Emits>()

const contentRef = ref<HTMLElement | null>(null)
const contentHeight = computed(() =>
  fullscreen.value ? contentHeightFullscreen.value : contentHeightNormal.value
)
const contentHeightNormal = ref(0)
const contentHeightFullscreen = ref(0)
const showInfo = ref(false)
const fullscreen = ref(false)
const initialRecord = ref(false)
const isLoadingAfterRecord = computed(
  () => props.isLoading !== undefined && initialRecord.value && props.isLoading
)
const windowWidth = useWindowWidth()

const isMobile = computed(() => {
  return windowWidth.value < 768
})

onMounted(() => {
  setTimeout(() => {
    recordContentHeight()
  }, 100)
})

watch(fullscreen, () => {
  setTimeout(() => {
    recordContentHeight()
  }, 100)
})

function recordContentHeight() {
  if (showInfo.value) return
  if (fullscreen.value) {
    contentHeightFullscreen.value = contentRef.value?.clientHeight ?? 0
  } else {
    contentHeightNormal.value = contentRef.value?.clientHeight ?? 0
  }
  initialRecord.value = true
}

function onToggleInfoClick() {
  recordContentHeight()
  showInfo.value = !showInfo.value
  emit('toggleShowInfo', showInfo.value)
}

function onToggleFullscreenClick() {
  recordContentHeight()
  fullscreen.value = !fullscreen.value
  emit('toggleFullscreen', fullscreen.value)
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/mixins.scss';

.column {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.card-interactive-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.icon {
  width: 16px;
  height: 16px;
  color: variables.$color-greyscale-secondary;
  &.fullscreen {
    width: 24px;
    height: 24px;
  }
}
.card-interactive-footer.fullscreen {
  display: flex;
  flex-direction: column;
}
.card-interactive-footer {
  display: flex;
  flex-direction: column;
}
.card-interactive-footer-content {
  display: flex;
  justify-content: flex-end;
}
.card-interactive-footer-controller {
  @include font-mixins.desktop-bold-label;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}
.card-interactive-controls {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: variables.$gap-medium;
  &.fullscreen {
    gap: 24px;
  }
}

svg {
  color: variables.$color-greyscale-secondary;
}
.card-interactive {
  display: grid;
  // flex-direction: column;
  grid-template-rows: auto 1fr auto;
  overflow: hidden;
  width: 100%;
  height: 100%;

  &.fullscreen {
    position: fixed;
    // max-width: 100vw;
    width: auto;
    height: auto;
    $padding: 8px;
    top: calc($padding + 64px);
    left: $padding;
    right: $padding;
    bottom: $padding;
    // transform: translate(-50%, -50%);
    border-radius: 8px;
    border: 1px;
    z-index: 2;
    gap: 16px;
    padding: 24px;
  }

  @media screen and (min-width: 768px) {
    &.fullscreen {
      $padding: 32px;
      top: calc($padding + 64px);
      left: $padding;
      right: $padding;
      bottom: $padding;
    }
  }

  @media screen and (min-width: 1024px) {
    &.fullscreen {
      $padding: 80px;
      top: calc($padding + 64px);
      left: $padding;
      right: $padding;
      bottom: $padding;
    }
  }
}

.content-container {
  @include mixins.scrollbar-vertical;
  min-height: 0;
  overflow-y: auto;

  @media screen and (min-width: 768px) {
    overflow-y: hidden;
  }
}
.info-container {
  @include font-mixins.desktop-regular-label;
  overflow-y: auto;
  overflow-x: hidden;
  max-width: 600px;
  max-height: 80vh;
  @include mixins.scrollbar-vertical;
}
.loader-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
.overlay {
  background: rgba(0, 0, 0, 0.5);
  height: 100%;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1;
}

.placeholder {
  width: 240px;
  height: 24px;
  background-color: bisque;
}

.vertical-divider {
  width: 1px;
  height: 24px;
  background-color: #e0e0e0;
}

.placeholder-card {
  width: 100%;

  .placeholder-content {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.horizontal-divider {
  width: 100%;
  height: 1px;
  margin-bottom: 8px;
  padding-top: 1px;
  background-color: #e0e0e0;
}
</style>
